import { useCallback, useContext, useMemo } from 'react'
import { useAuth } from '../App/AuthProvider'
import {
  ExitToApp as IconExitToApp,
  AccountCircleRounded as IconAccountCircleRounded,
  ManageAccountsRounded as IconManageAccountsRounded,
  Tune as IconTune,
} from '@mui/icons-material'
import useCurrentUserIsStudentSelector from '../../api/selectors/userRoleSelectors/useCurrentUserIsStudentSelector'
import { useNavigate } from 'react-router-dom'
import { ROUTE_MANAGEMENT, ROUTE_PROFILE } from '../../routes/constants'
import useToggleUserPopOver from '@derolfgroep/ui-components/src/components/UserNavigation/useToggleUserPopOver'
import { remoteViewContext } from '../../remoteView/v2/RemoteViewProvider'
import useAlertTeacherOffline from '../../remoteView/v2/useAlertTeacherOffline'
import useCurrentUserIsAuthPersonTypes from '../../api/selectors/useCurrentUserIsAuthPersonTypes'
import { PERSON_TYPES_MANAGEMENT } from '../../constants'
import { ACTION_LOGOUT } from '@derolfgroep/student-monitor-constants'
import useWebsocketLoggedIn from '../../remoteView/v2/useWebsocketLoggedIn'
import useSendExtensionMessage from '../../remoteView/v2/useSendExtensionMessage'
import { isChrome } from '../../utils/browers'

const handleNavigateToAccounts = () => window.open(process.env.ACCOUNTS_URL, '_blank', 'noopener,noreferrer')

export const useUserNavigationItems = () => {
  const userIsStudent = useCurrentUserIsStudentSelector()
  const userIsManagementTeam = useCurrentUserIsAuthPersonTypes(PERSON_TYPES_MANAGEMENT)
  const { handleClose } = useToggleUserPopOver()
  const navigate = useNavigate()
  const { handleLogout } = useAuth()
  const { studentMonitor } = useContext(remoteViewContext)
  const alertTeacherOffline = useAlertTeacherOffline()
  const { setLoggedIn } = useWebsocketLoggedIn()
  const sendExtensionMessage = useSendExtensionMessage()

  const userIsOnChromeOS = navigator?.userAgent?.includes('CrOS') === true

  const handleNavigateToProfile = useCallback(() => {
    navigate(ROUTE_PROFILE)
    handleClose()
  }, [handleClose, navigate])
  const handleNavigateToManagement = useCallback(() => {
    navigate(ROUTE_MANAGEMENT)
    handleClose()
  }, [handleClose, navigate])

  const handleAppAndExtensionLogout = useCallback(() => {
    if (userIsStudent && isChrome() && typeof chrome?.runtime !== 'undefined') {
      // Tell the extension to do logout stuff
      sendExtensionMessage({ action: ACTION_LOGOUT })
    }

    if (!userIsStudent) {
      // Alert the students the teacher is offline
      alertTeacherOffline()
    }

    // Set state to not logged in
    setLoggedIn(false)
    // Actually remove session data
    handleLogout()
    // Gracefully close websocket connection
    studentMonitor?.gateway?.close()
  }, [userIsStudent, handleLogout, alertTeacherOffline, sendExtensionMessage, setLoggedIn, studentMonitor?.gateway])

  return useMemo(
    () =>
      [
        {
          label: 'Profiel',
          handleClick: handleNavigateToProfile,
          icon: <IconAccountCircleRounded fontSize="small" />,
          analytics: 'userMenuNavigateToProfile',
        },
        !userIsStudent && {
          label: 'Account',
          icon: <IconManageAccountsRounded fontSize="small" />,
          handleClick: handleNavigateToAccounts,
          analytics: 'userMenuNavigateToAccounts',
        },
        userIsManagementTeam && {
          label: 'Beheer',
          icon: <IconTune fontSize="small" />,
          handleClick: handleNavigateToManagement,
          analytics: 'userMenuNavigateToManagement',
        },
        !userIsOnChromeOS && {
          label: 'Uitloggen',
          icon: <IconExitToApp fontSize="small" />,
          handleClick: handleAppAndExtensionLogout,
          analytics: 'userMenuUserLogout',
        },
      ].filter(Boolean),
    [
      handleAppAndExtensionLogout,
      handleNavigateToManagement,
      handleNavigateToProfile,
      userIsManagementTeam,
      userIsStudent,
      userIsOnChromeOS,
    ]
  )
}
